import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useData } from '@hooks/useData';
import { useTranslation } from 'react-i18next';

const ENDPOINT_URL = '/users/update-user';
const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type UpdateUserInput = paths[url]['put']['requestBody']['content']['application/json'];

export interface UpdateUserSuccessResponse {
    data: paths[url]['put']['responses']['200']['content']['application/json'];
}

export const useUpdateUser = () => {
    const { t } = useTranslation();
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setUser } = useData();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [validationErrors, setValidationErrors] = useState<any | null>(null);

    const request = async (props: UpdateUserInput) => {
        setLoading(true);
        setError(null);
        setValidationErrors(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify({ ...getCommonParameters(), ...props }),
            });

            const data = await response.json();

            if (!response.ok) {
                const errorMessage = data.message || t('common.error-occurred');
                const error = new Error(errorMessage) as ApiError;
                error.statusCode = response.status;
                error.errors = data.errors;

                throw error;
            }

            setUser(data as UpdateUserSuccessResponse);
        } catch (err: any) {
            handleCommonErrors(err);
            err.message && setError(err.message);
            err.errors && setValidationErrors(err.errors);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, lastResponseTime, validationErrors };
};
